import React, { useEffect, useState } from "react";
import api from "../../api";
import EmployeeHeader from "../components/Employee/EmployeeHeader";
import {
 BarChart,
 Bar,
 XAxis,
 YAxis,
 Tooltip,
 CartesianGrid,
 LineChart,
 Line,
 PieChart,
 Pie,
 Cell,
 Legend,
 ResponsiveContainer
} from "recharts";

const EmployeePerformance = () => {
 const [loading, setLoading] = useState(true);
 const [utilization, setUtilization] = useState([]);
 const [productivity, setProductivity] = useState([]);
 const [roles, setRoles] = useState([]);
 const [topDaily, setTopDaily] = useState([]);
 const [topWeekly, setTopWeekly] = useState([]);
 const [mode, setMode] = useState("daily");

 // responsive state
 const [isSmall, setIsSmall] = useState(window.innerWidth < 992);

 const fetchPerformance = async () => {
 try {
 const res = await api.get("/employees/stats/performance");

 if (res.data.success) {
 setUtilization(res.data.utilization || []);
 setProductivity(res.data.productivityTrend || []);
 setRoles(res.data.roles || []);
 setTopDaily(res.data.topDailyPerformers || []);
 setTopWeekly(res.data.topWeeklyPerformers || []);
 }
 } catch (err) {
 console.error("Performance fetch error:", err);
 } finally {
 setLoading(false);
 }
 };

 useEffect(() => {
 fetchPerformance();
 }, []);

 // Handle window resize for responsive UI
 useEffect(() => {
 const onResize = () => setIsSmall(window.innerWidth < 992);
 window.addEventListener("resize", onResize);
 return () => window.removeEventListener("resize", onResize);
 }, []);

 if (loading) return <div style={{ padding: 24 }}>Loading...</div>;

 const currentPerformers = mode === "daily" ? topDaily : topWeekly;

 // ======== Dynamic Bar Color Logic ========
 const getBarColor = (workload) => {
 if (workload <= 100) return "#28a745"; // green
 if (workload > 100 && workload <= 130) return "#fd7e14"; // orange
 return "#dc3545"; // red
 };

 return (
 <div style={{ padding: "24px 32px", maxWidth: "100%", width: "100%" }}>
 <EmployeeHeader />

 {/* ===== TOP GRID ===== */}
 <div
 style={{
 display: "grid",
 gap: "20px",
 marginTop: "16px",
 width: "100%",
 gridTemplateColumns: isSmall ? "1fr" : "1fr 1fr",
 }}
 >
 {/* Utilization */}
 <div style={cardStyle}>
 <div>
 <h6 style={titleStyle}>Team Utilization</h6>
 <small style={subTitleStyle}>Workload (supports &gt;100%)</small>
 </div>

 <div style={{ width: "100%", height: 280, marginTop: "10px" }}>
 <ResponsiveContainer>
 <BarChart data={utilization} layout="vertical">
 <CartesianGrid strokeDasharray="3 3" />
 <XAxis type="number" domain={[0, 200]} />
 <YAxis type="category" dataKey="name" width={80} />
 <Tooltip />
 <Bar
 dataKey="workload"
 shape={(props) => {
 const color = getBarColor(props.payload.workload);
 return <rect {...props} fill={color} radius={[0, 4, 4, 0]} />;
 }}
 />
 </BarChart>
 </ResponsiveContainer>
 </div>
 </div>

 {/* Productivity Trend */}
 <div style={cardStyle}>
 <h6 style={titleStyle}>Team Productivity Trend</h6>
 <small style={subTitleStyle}>Weekly performance score</small>

 <div style={{ width: "100%", height: 280, marginTop: "10px" }}>
 <ResponsiveContainer>
 <LineChart data={productivity}>
 <CartesianGrid strokeDasharray="3 3" />
 <XAxis dataKey="week" />
 <YAxis domain={[0, 100]} />
 <Tooltip />
 <Line
 type="monotone"
 dataKey="score"
 stroke="#000"
 strokeWidth={2}
 dot={{ r: 3 }}
 />
 </LineChart>
 </ResponsiveContainer>
 </div>
 </div>
 </div>

 {/* ===== BOTTOM GRID ===== */}
 <div
 style={{
 display: "grid",
 gap: "20px",
 marginTop: "20px",
 width: "100%",
 gridTemplateColumns: isSmall ? "1fr" : "1fr 300px",
 }}
 >
 {/* Role Distribution */}
 <div style={cardStyle}>
 <h6 style={titleStyle}>Role Distribution</h6>

 <div style={{ width: "100%", height: 300 }}>
 <ResponsiveContainer>
 <PieChart>
 <Pie
 data={roles}
 dataKey="value"
 nameKey="name"
 cx="50%"
 cy="50%"
 innerRadius={60}
 outerRadius={95}
 label={(entry) => `${entry.name}: ${entry.value}`}
 >
 {roles.map((entry, index) => (
 <Cell key={index} fill={entry.color || "#8884d8"} />
 ))}
 </Pie>
 <Legend />
 </PieChart>
 </ResponsiveContainer>
 </div>
 </div>

 {/* Top Performers */}
 <div style={cardStyle}>
 <h6 style={titleStyle}>Top Performers</h6>

 <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
 <button
 onClick={() => setMode("daily")}
 style={{
 ...toggleBtn,
 background: mode === "daily" ? "#000" : "#fff",
 color: mode === "daily" ? "#fff" : "#000"
 }}
 >
 Daily
 </button>
 <button
 onClick={() => setMode("weekly")}
 style={{
 ...toggleBtn,
 background: mode === "weekly" ? "#000" : "#fff",
 color: mode === "weekly" ? "#fff" : "#000"
 }}
 >
 Weekly
 </button>
 </div>

 <ul style={{ marginTop: 14, padding: 0, listStyle: "none" }}>
 {currentPerformers.map((p, index) => (
 <li
 key={index}
 style={{
 display: "flex",
 alignItems: "center",
 marginBottom: 14
 }}
 >
 <div style={{ fontWeight: 700, width: 22 }}>#{index + 1}</div>
 <img
 src={p.image || `https://i.pravatar.cc/40?u=${p.name}`}
 onError={(e) => (e.target.src = `https://i.pravatar.cc/40?u=${p.name}`)}
 alt=""
 style={{ width: 32, height: 32, borderRadius: "50%", marginRight: 10 }}
 />
 <div style={{ flex: 1 }}>
 <div style={{ fontWeight: 600 }}>{p.name}</div>
 <small style={{ color: "#6c757d" }}>{p.role}</small>
 </div>
 <div style={{ fontWeight: 700 }}>
 {mode === "daily" ? p.scoreDaily : p.scoreWeekly}
 </div>
 </li>
 ))}
 </ul>
 </div>
 </div>
 </div>
 );
};

/* ===== STYLES ===== */
const cardStyle = {
 background: "#fff",
 border: "1px solid #e4e8ef",
 borderRadius: "10px",
 padding: "16px"
};

const titleStyle = {
 fontWeight: 600,
 marginBottom: "2px"
};

const subTitleStyle = {
 color: "#6c757d",
 fontSize: "12px"
};

const toggleBtn = {
 padding: "6px 12px",
 borderRadius: "6px",
 border: "1px solid #000",
 cursor: "pointer",
 fontSize: "12px"
};

export default EmployeePerformance;
